import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from './useAuth'

// ── EVENTS (This Week "Coming up") ──────────────────────────────────────
// Church-scoped events with an "I'm going" RSVP toggle, read from the
// unified `church_events` table (events/event_attendees unification —
// see supabase-schema-events-unify.sql). Recurring events are stored as a
// single row and expanded into their occurrences within a date window
// client-side — simplest correct approach without a server-side job.
// `event_exceptions` lets an elder/admin cancel or override a single
// occurrence without touching the series.

const DAY_MS = 24 * 60 * 60 * 1000

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function clampDay(year, month, day) {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(day, lastDay))
}

// Which "Nth weekday of month" a date represents, e.g. the 3rd Tuesday -> 3.
function weekdayOrdinal(date) {
  return Math.ceil(date.getDate() / 7)
}

function nthWeekdayOfMonth(year, month, weekday, n) {
  const first = new Date(year, month, 1)
  const firstWeekday = first.getDay()
  const day = 1 + ((weekday - firstWeekday + 7) % 7) + (n - 1) * 7
  const lastDay = new Date(year, month + 1, 0).getDate()
  if (day > lastDay) return null
  return new Date(year, month, day)
}

// Expand one church_events row into every occurrence between windowStart
// and windowEnd (inclusive), respecting repeat_until and all 5 recurrence
// rules. Returns Date objects at local midnight.
function expandOccurrences(ev, windowStart, windowEnd) {
  const base = new Date(`${ev.event_date}T00:00:00`)
  if (Number.isNaN(base.getTime())) return []
  const until = ev.repeat_until ? new Date(`${ev.repeat_until}T23:59:59`) : null
  const withinUntil = (d) => !until || d <= until

  if (ev.recurrence === 'none' || !ev.recurrence) {
    return (base >= windowStart && base <= windowEnd && withinUntil(base)) ? [base] : []
  }

  const out = []

  if (ev.recurrence === 'weekly' || ev.recurrence === 'fortnightly') {
    const stepDays = ev.recurrence === 'fortnightly' ? 14 : 7
    let d = new Date(base)
    if (d < windowStart) {
      const missed = Math.ceil((windowStart - d) / (stepDays * DAY_MS))
      d = new Date(d.getTime() + missed * stepDays * DAY_MS)
    }
    while (d <= windowEnd) {
      if (d >= windowStart && withinUntil(d)) out.push(new Date(d))
      d = new Date(d.getTime() + stepDays * DAY_MS)
    }
    return out
  }

  if (ev.recurrence === 'monthly_date') {
    const day = base.getDate()
    for (let monthsAhead = 0; monthsAhead < 36; monthsAhead++) {
      const monthStart = new Date(base.getFullYear(), base.getMonth() + monthsAhead, 1)
      if (monthStart > windowEnd) break
      const occ = clampDay(base.getFullYear(), base.getMonth() + monthsAhead, day)
      if (occ >= windowStart && occ <= windowEnd && withinUntil(occ)) out.push(occ)
    }
    return out
  }

  if (ev.recurrence === 'monthly_weekday') {
    const weekday = base.getDay()
    const n = weekdayOrdinal(base)
    for (let monthsAhead = 0; monthsAhead < 36; monthsAhead++) {
      const monthStart = new Date(base.getFullYear(), base.getMonth() + monthsAhead, 1)
      if (monthStart > windowEnd) break
      const occ = nthWeekdayOfMonth(base.getFullYear(), base.getMonth() + monthsAhead, weekday, n)
      if (occ && occ >= windowStart && occ <= windowEnd && withinUntil(occ)) out.push(occ)
    }
    return out
  }

  return []
}

export function useEvents(churchIdOverride) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const { user, profile } = useAuth()
  const churchId = churchIdOverride || profile?.church_id

  const fetch = useCallback(async () => {
    if (!churchId) { setEvents([]); setLoading(false); return }
    setLoading(true)
    const now = new Date(); now.setHours(0, 0, 0, 0)
    const weekAhead = new Date(now.getTime() + 7 * DAY_MS)

    const [evRes, exRes] = await Promise.all([
      supabase.from('church_events')
        .select('*, event_attendees(user_id, occurrence_date, profiles(full_name, avatar_url))')
        .eq('church_id', churchId)
        .order('event_date', { ascending: true }),
      supabase.from('event_exceptions')
        .select('event_id, occurrence_date, cancelled, override_time, override_location, override_title')
        .gte('occurrence_date', ymd(now)).lte('occurrence_date', ymd(weekAhead)),
    ])
    if (evRes.error) {
      console.error('useEvents load:', evRes.error)
      setEvents([])
      setLoading(false)
      return
    }
    const exceptionsByKey = new Map()
    for (const ex of (exRes.data || [])) exceptionsByKey.set(`${ex.event_id}:${ex.occurrence_date}`, ex)

    const rows = (evRes.data || []).flatMap(ev => {
      const attendees = ev.event_attendees || []
      const occurrences = expandOccurrences(ev, now, weekAhead)
      return occurrences.map(date => {
        const dateStr = ymd(date)
        const exception = exceptionsByKey.get(`${ev.id}:${dateStr}`)
        if (exception?.cancelled) return null
        const occAttendees = attendees.filter(a => a.occurrence_date === dateStr)
        const attendeeIds = occAttendees.map(a => a.user_id)
        const profilesById = {}
        for (const a of occAttendees) if (a.profiles) profilesById[a.user_id] = a.profiles
        return {
          id:          ev.id,
          occursAt:    date,
          occurrenceDate: dateStr,
          title:       exception?.override_title || ev.name,
          description: ev.description,
          time:        exception?.override_time || ev.event_time,
          location:    exception?.override_location || ev.location,
          recurring:   ev.recurrence,
          repeatUntil: ev.repeat_until,
          hasException: !!exception,
          goingIds:    attendeeIds,
          goingProfilesById: profilesById,
          goingCount:  attendeeIds.length,
          going:       user ? attendeeIds.includes(user.id) : false,
        }
      }).filter(Boolean)
    }).sort((a, b) => a.occursAt - b.occursAt)
    setEvents(rows)
    setLoading(false)
  }, [churchId, user])

  useEffect(() => { fetch() }, [fetch])

  async function toggleGoing(eventId, occurrenceDate, currentlyGoing) {
    if (!user) return { error: 'Sign in to RSVP.' }
    // Optimistic local update — refetch below reconciles with the DB.
    setEvents(curr => curr.map(e => (e.id !== eventId || e.occurrenceDate !== occurrenceDate) ? e : ({
      ...e,
      going: !currentlyGoing,
      goingCount: Math.max(0, e.goingCount + (currentlyGoing ? -1 : 1)),
      goingIds: currentlyGoing ? e.goingIds.filter(id => id !== user.id) : [...e.goingIds, user.id],
      goingProfilesById: currentlyGoing
        ? e.goingProfilesById
        : { ...e.goingProfilesById, [user.id]: { full_name: profile?.full_name, avatar_url: profile?.avatar_url } },
    })))
    if (currentlyGoing) {
      const { error } = await supabase.from('event_attendees')
        .delete().eq('event_id', eventId).eq('user_id', user.id).eq('occurrence_date', occurrenceDate)
      if (error) { console.warn('toggleGoing un-RSVP:', error.message); fetch(); return { error: error.message } }
    } else {
      const { error } = await supabase.from('event_attendees')
        .insert({ event_id: eventId, user_id: user.id, occurrence_date: occurrenceDate })
      if (error) { console.warn('toggleGoing RSVP:', error.message); fetch(); return { error: error.message } }
    }
    return { error: null }
  }

  return { events, loading, toggleGoing, refresh: fetch }
}
